import React from "react";

/**
 * VideoPlayer — universal video renderer for listings, reels & POIs.
 *
 * Detects the URL type and renders the right element:
 *   · YouTube (youtube.com/watch?v= or youtu.be/…)   → iframe embed
 *   · Vimeo (vimeo.com/…)                            → iframe embed
 *   · Direct video (.mp4, .webm, .mov, .ogv)         → <video controls />
 *   · Anything else                                  → returns null
 *
 * Reused by ListingDetail (Slice b), future Reels page (Slice c),
 * and any other surface that needs a single-URL video.
 */
export default function VideoPlayer({ url, className = "", testid = "video-player", autoPlay = false }) {
  if (!url || typeof url !== "string") return null;

  const yt = _extractYoutubeId(url);
  if (yt) {
    return (
      <div className={`aspect-video rounded-2xl overflow-hidden bg-black ${className}`} data-testid={testid}>
        <iframe
          src={`https://www.youtube.com/embed/${yt}${autoPlay ? "?autoplay=1&mute=1" : ""}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
          data-testid={`${testid}-iframe`}
        />
      </div>
    );
  }

  const vm = _extractVimeoId(url);
  if (vm) {
    return (
      <div className={`aspect-video rounded-2xl overflow-hidden bg-black ${className}`} data-testid={testid}>
        <iframe
          src={`https://player.vimeo.com/video/${vm}${autoPlay ? "?autoplay=1&muted=1" : ""}`}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Vimeo video"
          data-testid={`${testid}-iframe`}
        />
      </div>
    );
  }

  if (/\.(mp4|webm|mov|ogv|m3u8)(\?|$)/i.test(url)) {
    return (
      <video
        src={url}
        controls
        playsInline
        preload="metadata"
        autoPlay={autoPlay}
        muted={autoPlay}
        className={`w-full aspect-video rounded-2xl bg-black object-contain ${className}`}
        data-testid={`${testid}-video`}
      />
    );
  }

  return null;
}

function _extractYoutubeId(url) {
  try {
    const u = new URL(url);
    if (/youtube\.com$/i.test(u.hostname) || /youtu\.be$/i.test(u.hostname)) {
      if (u.hostname === "youtu.be") return u.pathname.slice(1) || null;
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(embed|shorts)\/([\w-]+)/);
      if (m) return m[2];
    }
  } catch { /* not a URL */ }
  return null;
}

function _extractVimeoId(url) {
  try {
    const u = new URL(url);
    if (/vimeo\.com$/i.test(u.hostname)) {
      const m = u.pathname.match(/^\/(\d+)/);
      if (m) return m[1];
    }
  } catch { /* silent */ }
  return null;
}
